<script setup>
import {nextTick, onBeforeMount, reactive, ref} from "vue";
import {debounceFn} from "@/hooks/debounce.js";
import router from "@/router/index.js";
import {getRoute} from "@/hooks/getRoute.js";
import {userInfoStore} from "@/store/store.js";
import moment from "moment";
import BoxUpload from "@/components/YTBoxUpload/newIndex.vue";
import {listTenant} from "@/api/user/user.js";
import {listBizCategoryTree} from "@/api/bizCategory/bizCategory.js";
import {detailGoodsCards} from "@/api/goodsCards/goodsCards.js"
import {getQrcodePrizeCodesAvailable, singleQrcodePrizeCodes} from "@/api/prizeCodes/prizeCodes.js"
import {listItemByCategory, bindGoodsCards} from "@/api/goods/goods.js"

const { route } = getRoute()
const info = userInfoStore().info
const creatorName = info.nickname ? info.nickname : info.username

/*表单数据*/
const formData = reactive({
  goodsCardNo: null, // 卡片编号（创建模式为 null）
  applyTime: null, // 申请日期
  creator: creatorName, // 创建人（当前用户昵称）
  goodsCardCrTime: null, // 创建日期
  barCode: "", // 卡片二维码编号
  goodsCardImage: [], // 卡片电子图片
  remark: "", // 备注
})

// 卡片电子图片上传锚点
const boxUploadRef = ref()
const formRef = ref()
const handleGoodsCardImageSuccess = (url) => {
  formData.goodsCardImage = url
  formRef.value?.validateField('goodsCardImage')
}
const handleGoodsCardImageRemove = (url) => {
  formData.goodsCardImage = url
  formRef.value?.validateField('goodsCardImage')
}

/*表单校验规则*/
const rules = {
  barCode: [{ required: true, message: "请上传卡片二维码", trigger: "change" }],
  goodsCardImage: [{ required: true, message: "请上传卡片电子图片", trigger: "change" }],
}

/*二维码展示弹窗*/
const openQrCodeDialog = ref(false)
const qrCodeImageUrl = ref("")
const qrCodeLoading = ref(false)

const handleOpenQrCode = async () => {
  openQrCodeDialog.value = true
  if (!formData.barCode || qrCodeImageUrl.value) return
  try {
    qrCodeLoading.value = true
    const res = await singleQrcodePrizeCodes({ barCode: formData.barCode })
    const url = URL.createObjectURL(res.data)
    qrCodeImageUrl.value = url
  } catch (e) {
    console.log(e)
  } finally {
    qrCodeLoading.value = false
  }
}

const handleDownloadQrCode = async () => {
  if (!formData.barCode) return
  try {
    const res = await singleQrcodePrizeCodes({ barCode: formData.barCode })
    const blob = new Blob([res.data])
    const link = document.createElement("a")
    link.href = URL.createObjectURL(blob)
    link.setAttribute("download", `${formData.barCode}.png`)
    document.body.appendChild(link)
    link.click()
    setTimeout(() => {
      document.body.removeChild(link)
      URL.revokeObjectURL(link.href)
    }, 100)
  } catch (e) {
    console.log(e)
  }
}

/*弹窗筛选条件*/
const filterOnlyOnSale = ref(false) // 仅上架商品
const productKeyword = ref("") // 商品关键字搜索
const dialogFilterChange = () => {
  if (selectedBizCategoryId.value !== undefined) {
    getDialogTableList(true)
  }
}
const debouncedFilterChange = debounceFn(dialogFilterChange, 400)
const handleAddItem = () => {
  window.open(window.location.origin + "/addItem", "_blank") // 新开页面跳转到添加商品
}

/*商品类目树*/
const categoryTreeData = ref([])
const categoryTreeLoading = ref(false)
const selectedBizCategoryId = ref(null)
const treeProps = {
  children: "children",
  label: "bizCategoryName",
}
// 处理树节点点击
const handleTreeClick = (data) => {
  const hasChildren = data.children && data.children.length > 0
  console.log("[TreeClick] node:", data.bizCategoryName, "| bizCategoryId:", data.bizCategoryId, "| hasChildren:", hasChildren, "| children:", data.children)
  if (!hasChildren) {
    selectedBizCategoryId.value = data.bizCategoryId
    console.log("[TreeClick] set selectedBizCategoryId:", data.bizCategoryId)
    getDialogTableList(true)
  }
}
const handleGetCategoryTree = async () => {
  try {
    categoryTreeLoading.value = true
    const apiData = await listBizCategoryTree()
    categoryTreeData.value = [
      {
        bizCategoryName: "商品类目",
        level: 1,
        children: apiData,
      },
      {
        bizCategoryName: "无商品类目",
        level: 1,
        children: [],
        bizCategoryId: null,
      }
    ]
    console.log("[CategoryTree] built tree data:", JSON.parse(JSON.stringify(categoryTreeData.value)))
    categoryTreeLoading.value = false
  } catch (e) {
    console.log(e)
    categoryTreeLoading.value = false
  }
}

// 绑定奖品弹窗
const openBindPrizeDialog = ref(false)
const handleOpenBindPrize = () => {
  openBindPrizeDialog.value = true
  handleGetCategoryTree()
  nextTick(() => {
    bindDialogTableScroll()
  })
}
const handleCloseBindPrize = () => {
  openBindPrizeDialog.value = false
  // 清空过滤器
  productKeyword.value = ""
  tenantSelectKey.value = undefined
  cacheTenant.value = { name: null, tenantId: null }
  filterOnlyOnSale.value = false
  // 清空类目选择与表格
  selectedBizCategoryId.value = null
  dialogPrizeData.value = []
  dialogTablePage.value = 1
  dialogTableHasNext.value = false
  selectedPrizeRows.value = []
  if (dialogTableScrollWrapper) {
    dialogTableScrollWrapper.removeEventListener("scroll", handleDialogTableScroll)
    dialogTableScrollWrapper = null
  }
}
const handleSelectPrize = () => {
  if (selectedPrizeRows.value.length > 0) {
    const row = { ...selectedPrizeRows.value[0] }
    row.quantity = 0
    prizeTableData.value = [row]
  }
  handleCloseBindPrize()
}

/*弹窗-商品所属商家选择*/
const tenantLoading = ref(false)
const tenantListArr = ref([])
const tenantTotal = ref(0)
const tenantPage = ref(1)
const tenantPageSize = ref(5)
const tenantSelectKey = ref()
const cacheTenant = ref({ name: null, tenantId: null })
// 获取商家列表
const handleGetTenant = async () => {
  try {
    const page = { page: tenantPage.value, pageSize: tenantPageSize.value }
    tenantLoading.value = true
    const res = await listTenant(page, { keyword: tenantSelectKey.value || null })
    tenantListArr.value = res.list
    tenantTotal.value = parseInt(res.total)
    tenantLoading.value = false
  } catch (e) {
    console.log(e)
    tenantLoading.value = false
  }
}
// 选择商家
const handleSelectTenant = (e) => {
  cacheTenant.value = tenantListArr.value.find(i => i.tenantId === e) || { name: null, tenantId: e }
  dialogFilterChange()
}
// 展开下拉时刷新列表
const handleClickSelect = (e) => {
  if (e) handleGetTenant()
}
// 远程搜索商家
const handleRemoteSearchTenant = (keyword) => {
  tenantSelectKey.value = keyword
  tenantPage.value = 1
  handleGetTenant()
}
// 商家分页切换
const handleChangeTenantPage = () => {
  handleGetTenant()
}

/*奖品表格数据（主表格）*/
const prizeTableData = ref([])
const handleRemovePrize = () => {
  prizeTableData.value = []
}
/*弹窗商品列表（类目查询结果）*/
const dialogPrizeData = ref([])
const selectedPrizeRows = ref([])
const dialogTableRef = ref()
const handlePrizeSelectionChange = (selection) => {
  if (selection.length > 1) {
    const current = selection[selection.length - 1]
    dialogTableRef.value?.clearSelection()
    nextTick(() => {
      dialogTableRef.value?.toggleRowSelection(current, true)
    })
    selectedPrizeRows.value = [current]
    return
  }
  selectedPrizeRows.value = selection
}

/*商品规格选择*/
const specPopoverVisible = ref({})
const getRowKey = (row) => row.goodsName || "default"
const handleSpecCancel = (row) => {
  specPopoverVisible.value = { ...specPopoverVisible.value, [getRowKey(row)]: false }
}
const handleSpecTagClick = (row, group, itemId) => {
  // 同组内单选：先将组内所有 item.selected 置为 false
  group.propertyGroupItemList?.forEach(gItem => {
    gItem.selected = false
  })
  // 再设置当前选中
  const target = group.propertyGroupItemList?.find(i => i.propertyGroupItemId === itemId)
  if (target) target.selected = true
}
const isSpecSelected = (row, itemId) => {
  let found = false
  row.propertyGroupList?.forEach(group => {
    group.propertyGroupItemList?.forEach(item => {
      if (item.propertyGroupItemId === itemId && item.selected) {
        found = true
      }
    })
  })
  return found
}
const getSelectedSpecNames = (row) => {
  const names = []
  row.propertyGroupList?.forEach(group => {
    group.propertyGroupItemList?.forEach(item => {
      if (item.selected) {
        names.push(item.propertyName)
      }
    })
  })
  return names.join("、")
}

/*弹窗表格触底翻页*/
const dialogTablePage = ref(1)
const dialogTableHasNext = ref(false)
const dialogTableLoading = ref(false)
let dialogTableScrollWrapper = null

const handleDialogTableScroll = (e) => {
  const target = e.target
  if (target.scrollHeight - target.scrollTop - target.clientHeight > 24) return
  loadDialogTableNext()
}

const loadDialogTableNext = () => {
  if (!dialogTableHasNext.value || dialogTableLoading.value) return
  getDialogTableList(false)
}

const getDialogTableList = async (isReset = false) => {
  if (!isReset && (!dialogTableHasNext.value || dialogTableLoading.value)) return
  try {
    if (isReset) {
      dialogTablePage.value = 1
      dialogPrizeData.value = []
      dialogTableHasNext.value = false
    }
    dialogTableLoading.value = true
    const pageData = { page: dialogTablePage.value, pageSize: 20 }
    const param = {
      goodsName: productKeyword.value || null,
      tenantId: cacheTenant.value.tenantId || null,
    }
    if (filterOnlyOnSale.value) {
      param.onShelf = true
    }
    if (selectedBizCategoryId.value !== null && selectedBizCategoryId.value !== undefined) {
      param.bizCategoryId = selectedBizCategoryId.value
    }
    const res = await listItemByCategory(pageData, param)
    const list = res.list || []
    dialogPrizeData.value = isReset ? list : dialogPrizeData.value.concat(list)
    dialogTableHasNext.value = Boolean(res.hasNextPage)
    if (res.hasNextPage) dialogTablePage.value += 1
  } catch (e) {
    console.log(e)
  } finally {
    dialogTableLoading.value = false
  }
}

const bindDialogTableScroll = async () => {
  await nextTick()
  const wrapper = document.querySelector(".dialog-table-panel .el-table__body-wrapper")
  if (wrapper) {
    wrapper.addEventListener("scroll", handleDialogTableScroll, { passive: true })
    dialogTableScrollWrapper = wrapper
  }
}

/*页面状态*/
/*初始化*/
onBeforeMount(async () => {
  const goodsCardId = route.query.goodsCardId
  if (!goodsCardId) {
    // 创建模式：获取一个可用的二维码
    try {
      const res = await getQrcodePrizeCodesAvailable()
      if (res?.barCode) {
        formData.barCode = res.barCode
      }
    } catch (e) {
      console.log(e)
    }
    return
  }
  // 编辑模式：加载详情数据
  try {
    const res = await detailGoodsCards(goodsCardId)
    console.log("detailGoodsCards res", res)
    formData.goodsCardNo = res.goodsCardNo || ""
    formData.applyTime = res.applyTime || null
    formData.goodsCardCrTime = res.crTime || null
    formData.creator = res.crNickname || ""
    formData.barCode = res.barCode || ""
    formData.goodsCardImage = res.goodsCardImage ? [res.goodsCardImage] : []
    formData.remark = res.remark || ""
    nextTick(() => {
      boxUploadRef.value?.handleReview(formData.goodsCardImage)
    })
    prizeTableData.value = [{
      goodsId: res.goodsId,
      goodsName: res.goodsName || "",
      bizCategoryName: res.bizCategoryName || "",
      quantity: res.quantity ?? 0,
      unit: res.unit || "",
      price: res.price,
      properties: res.properties || [],
      propertyGroupList: res.propertyGroupList || [],
    }]
  } catch (e) {
    console.log(e)
  }
})

/*处理提交*/
const handleSubmit = async () => {
  const valid = await formRef.value?.validate().catch(() => false)
  if (!valid) {
    ElMessage.error("请根据提示修改表单信息")
    return
  }
  if (prizeTableData.value.length === 0) {
    ElMessage.warning("请先绑定商品")
    return
  }
  if (!formData.barCode) {
    ElMessage.warning("二维码编号不存在")
    return
  }
  try {
    const row = prizeTableData.value[0]
    const properties = []
    row.propertyGroupList?.forEach(group => {
      group.propertyGroupItemList?.forEach(item => {
        if (item.selected) {
          properties.push({
            propertyGroupId: group.propertyGroupId,
            propertyGroupItemId: item.propertyGroupItemId,
          })
        }
      })
    })
    const param = {
      barCode: formData.barCode,
      goodsCardImage: Array.isArray(formData.goodsCardImage) ? formData.goodsCardImage[0] : formData.goodsCardImage,
      goodsId: row.goodsId,
      quantity: row.quantity ?? 0,
      remark: formData.remark || "",
      properties,
    }
    await bindGoodsCards(param)
    ElMessage.success("绑定成功")
    router.back()
  } catch (e) {
    console.log(e)
  }
}
</script>

<template>
  <div class="container_box page-container mg_b_2">
    <div class="top-bar">
      <div class="flex_left">
        <v-btn variant="text" icon="mdi-chevron-left" @click="router.back()"></v-btn>
        <h2 class="page-title">寻宝卡</h2>
      </div>
      <div class="flex_right" style="gap: 12px;">
        <v-btn variant="elevated" @click="router.back()">
          <template v-slot:default>
            <div class="flex_bt">
              <v-icon style="margin-right: 4px;" color="#000">mdi-close</v-icon>
              <span>取消</span>
            </div>
          </template>
        </v-btn>
        <v-btn variant="elevated" color="success" @click="handleSubmit">
          <template v-slot:default>
            <div class="flex_bt">
              <v-icon style="margin-right: 4px;" color="#fff">mdi-check</v-icon>
              <span>提交</span>
            </div>
          </template>
        </v-btn>
      </div>
    </div>
    <div class="form-wrapper" style="background: #f5f5f8;">
      <el-form ref="formRef" :model="formData" :rules="rules" label-position="right" label-width="auto" class="add-form">
        <el-form-item label="卡片编号：">
          <span class="form-value">{{ formData.goodsCardNo || "—" }}</span>
        </el-form-item>

        <el-form-item label="申请日期：">
          <span class="form-value">{{ formData.applyTime ? moment(formData.applyTime).format("YYYY-MM-DD HH:mm") : "—" }}</span>
        </el-form-item>

        <el-form-item label="创建人：">
          <span class="form-value">{{ formData.creator }}</span>
        </el-form-item>

        <el-form-item label="创建日期：">
          <span class="form-value">{{ formData.goodsCardCrTime ? moment(formData.goodsCardCrTime).format("YYYY-MM-DD HH:mm") : "—" }}</span>
        </el-form-item>

        <el-form-item label="卡片二维码：" prop="barCode">
          <div class="flex_left" style="cursor: pointer; gap: 4px;" @click="handleOpenQrCode">
            <el-text type="primary">{{ formData.barCode || "暂无" }}</el-text>
            <v-icon color="#409eff">mdi-qrcode</v-icon>
          </div>
        </el-form-item>

        <el-form-item label="卡片电子图片：" prop="goodsCardImage" required>
          <div>
            <BoxUpload
                ref="boxUploadRef"
                :ali-oss-path="'GOODS_CARD_IMAGE'"
                :width="140"
                :height="140"
                :limit="1"
                :file-size="1"
                :isReplaceMode="true"
                @success="handleGoodsCardImageSuccess"
                @remove="handleGoodsCardImageRemove"
            />
          </div>
        </el-form-item>

        <el-form-item label="备注：">
          <el-input
              v-model="formData.remark"
              type="textarea"
              :rows="5"
              resize="none"
              placeholder="请输入备注"
              maxlength="500"
              show-word-limit
              style="width: 400px"
          />
        </el-form-item>
      </el-form>
    </div>
  </div>
  <div class="container_box" style="padding-bottom: 16px;">
    <div class="flex_left" style="gap: 24px;">
      <div class="flex_left" style="cursor: pointer;" @click="handleOpenBindPrize">
        <v-icon color="#409eff">mdi-folder-outline</v-icon>
        <el-text type="primary" style="margin-left: 4px;">{{ prizeTableData.length > 0 ? "更改绑定奖品" : "绑定奖品" }}</el-text>
      </div>
      <el-popconfirm
          width="288"
          @confirm="handleRemovePrize"
          title="确定删除该商品吗？"
      >
        <template #reference>
          <div class="flex_left" style="cursor: pointer;">
            <v-icon color="#409eff">mdi-trash-can-outline</v-icon>
            <el-text type="primary" style="margin-left: 4px;">消除商品</el-text>
          </div>
        </template>
        <template #actions="{ confirm, cancel }">
          <div class="flex_right">
            <v-btn class="mg_r_2" size="small" @click="cancel">取消</v-btn>
            <v-btn size="small" color="#796CB4" @click="confirm">确定</v-btn>
          </div>
        </template>
      </el-popconfirm>
    </div>
  </div>
  <div class="container_box" style="margin-top: 16px;">
    <el-table
        :data="prizeTableData"
        border
        stripe
        style="width: 100%"
        @selection-change="handlePrizeSelectionChange"
        :header-cell-style="{ background: '#f5f7fa', color: '#272539', fontWeight: 600 }"
        table-layout="auto"
    >
      <el-table-column type="selection" width="50" align="center" />
      <el-table-column label="商品类目" align="center">
        <template #default="{ row }">
          <span>{{ row.bizCategoryName || "—" }}</span>
        </template>
      </el-table-column>
      <el-table-column prop="goodsName" label="商品名称" align="center" />
      <el-table-column label="商品规格" align="center">
        <template #default="{ row }">
          <el-popover
              :visible="specPopoverVisible[getRowKey(row)]"
              placement="bottom-start"
              :offset="6"
              :show-arrow="false"
              :width="360"
              trigger="click"
              :hide-after="0"
              popper-style="boxShadow: 0px 1px 6px 0px rgba(25,24,27,0.22); borderRadius: 16px;"
              @update:visible="(val) => specPopoverVisible[getRowKey(row)] = val"
          >
            <template #reference>
              <div class="spec-trigger">
                <div class="el-select__wrapper flex_left" style="cursor: pointer; width: 100%;">
                  <div style="flex:1; text-align: left;">
                    <span class="spec-trigger-placeholder">{{ getSelectedSpecNames(row) || "选择规格" }}</span>
                  </div>
                  <img class="spec-trigger-arrow" src="@/assets/images/filter/select-arrowDown.svg" />
                </div>
              </div>
            </template>
            <div style="padding: 4px 0;">
              <div style="font-size: 14px; font-weight: 400; padding: 0 4px 12px; border-bottom: 1px solid #ddd; margin-bottom: 12px;">
                已选择：<span>{{ getSelectedSpecNames(row) }}</span>
              </div>
              <div style="max-height: 200px; overflow-y: auto;">
                <div v-for="(group, gIdx) in row.propertyGroupList" :key="gIdx" style="margin-bottom: 12px;">
                  <div class="spec-group-name">{{ group.groupName }}</div>
                  <div class="spec-tag-wrap">
                    <el-check-tag
                        v-for="(item, pIdx) in group.propertyGroupItemList"
                        :key="pIdx"
                        :checked="item.selected"
                        type="primary"
                        class="spec-check-tag"
                        @click="handleSpecTagClick(row, group, item.propertyGroupItemId)"
                    >
                      {{ item.propertyName }}
                    </el-check-tag>
                  </div>
                </div>
              </div>
              <div class="flex_right" style="gap: 12px; padding-top: 12px; border-top: 1px solid #eee; margin-top: 8px;">
                <v-btn size="small" variant="elevated" @click="handleSpecCancel(row)">取消</v-btn>
                <v-btn size="small" variant="elevated" color="primary" :disabled="!getSelectedSpecNames(row)">确定</v-btn>
              </div>
            </div>
          </el-popover>
        </template>
      </el-table-column>
      <el-table-column label="可兑换数量" align="center">
        <template #default="{ row }">
          <el-input-number v-model="row.quantity" :min="0" :precision="0" size="small" controls-position="right" style="width: 120px;" />
        </template>
      </el-table-column>
      <el-table-column label="单位" align="center">
        <template #default="{ row }">
          <el-select v-model="row.unit" disabled style="width: 100px;">
            <el-option :label="row.unit" :value="row.unit" />
          </el-select>
        </template>
      </el-table-column>
      <el-table-column label="单价" align="center">
        <template #default="{ row }">
          <span>{{ row.price !== undefined && row.price !== null ? "￥" + row.price : "—" }}</span>
        </template>
      </el-table-column>
      <el-table-column label="备注" align="center">
        <template #default="{ row }">
          <el-input v-model="row.remark" placeholder="备注" size="small" style="width: 120px;" />
        </template>
      </el-table-column>
    </el-table>
  </div>

  <el-dialog
      v-model="openQrCodeDialog"
      width="480"
      title="寻宝卡二维码"
      :close-on-click-modal="false"
  >
    <div style="text-align: center; padding: 20px 0;">
      <div style="font-size: 18px; font-weight: 600; color: #272539; margin-bottom: 16px;">{{ formData.barCode }}</div>
      <div style="margin-bottom: 20px; display: flex; justify-content: center;">
        <img v-if="qrCodeImageUrl" :src="qrCodeImageUrl" style="width: 160px; height: 160px;" />
        <span v-else-if="qrCodeLoading" style="color: #999;">加载中...</span>
        <span v-else style="color: #ccc;">暂无二维码</span>
      </div>
      <v-btn color="primary" variant="elevated" @click="handleDownloadQrCode">二维码下载</v-btn>
    </div>
  </el-dialog>

  <el-dialog
      v-model="openBindPrizeDialog"
      width="900"
      :close-on-click-modal="false"
      :show-close="false"
  >
    <template #header>
      <div style="text-align: center; font-size: 22px; font-weight: 500;">商品类目</div>
    </template>
    <div>
      <div class="flex_bt" style="gap: 16px; flex-wrap: wrap;">
        <div class="flex_left" style="gap: 8px; white-space: nowrap;">
          <div class="flex_left" style="gap: 8px; white-space: nowrap;">
            <span>关键字搜索商品：</span>
            <el-input v-model="productKeyword" placeholder="请输入关键词" clearable style="width: 200px;" @input="debouncedFilterChange" />
          </div>
          <el-select
              v-model="cacheTenant.tenantId"
              placeholder="商品所属商家"
              style="width: 200px;"
              :loading="tenantLoading"
              @change="handleSelectTenant"
              @click="handleClickSelect"
              filterable
              remote
              remote-show-suffix
              :remote-method="handleRemoteSearchTenant"
          >
            <el-option
                v-for="item in tenantListArr"
                :key="`tenantId${item.tenantId}`"
                :label="item.name"
                :value="item.tenantId"
            />
            <template v-if="!tenantLoading && !tenantListArr.some(res => res.tenantId === cacheTenant.tenantId) && cacheTenant.tenantId" #header>
              当前已选：
              <el-option
                  :label="cacheTenant.name"
                  :value="cacheTenant.tenantId"
              />
            </template>
            <template #footer>
              <div class="flex_center">
                <el-pagination
                    size="small"
                    background
                    layout="prev, pager, next"
                    :total="tenantTotal"
                    v-model:current-page="tenantPage"
                    v-model:page-size="tenantPageSize"
                    @current-change="handleChangeTenantPage"
                />
              </div>
            </template>
          </el-select>
          <el-checkbox v-model="filterOnlyOnSale" @change="dialogFilterChange">筛选：仅上架商品</el-checkbox>
        </div>
        <div class="flex_left" style="cursor: pointer;" @click="handleAddItem">
          <v-icon color="#409eff">mdi-plus</v-icon>
          <el-text type="primary" style="margin-left: 4px;">增加商品</el-text>
        </div>
      </div>
    </div>
    <div class="dialog-content">
      <div class="dialog-tree-panel">
        <el-tree
            :data="categoryTreeData"
            :props="treeProps"
            v-loading="categoryTreeLoading"
            default-expand-all
            empty-text="暂无商品类目"
            :highlight-current="true"
            @node-click="handleTreeClick"
        >
          <template #default="{ node, data }">
            <div class="custom-tree-node">
              <v-icon v-if="node.level === 1" color="#000" size="small" style="margin-right: 4px;">mdi-folder-outline</v-icon>
              <v-icon v-else-if="node.level === 2" color="#000" size="small" style="margin-right: 4px;">mdi-folder</v-icon>
              <span style="color: #000;">{{ node.label }}</span>
            </div>
          </template>
        </el-tree>
      </div>
      <div class="dialog-table-panel">
        <el-table
            ref="dialogTableRef"
            :data="dialogPrizeData"
            border
            stripe
            style="width: 100%"
            @selection-change="handlePrizeSelectionChange"
            :header-cell-style="{ background: '#f5f7fa', color: '#272539', fontWeight: 600 }"
        >
          <el-table-column type="selection" width="50" align="center" />
          <el-table-column prop="goodsName" label="商品名称" align="center" />
          <el-table-column label="商品类目" align="center">
            <template #default="{ row }">
              <span>{{ row.bizCategoryName || "—" }}</span>
            </template>
          </el-table-column>
          <el-table-column label="商品图片" align="center">
            <template #default="{ row }">
              <el-image
                  style="width: 50px; height: 50px"
                  :src="row.images?.[0] || ''"
                  fit="cover"
              >
                <template #error>
                  <div class="image-placeholder-sm">暂无</div>
                </template>
              </el-image>
            </template>
          </el-table-column>
          <template #append>
            <div v-if="dialogTableLoading" style="text-align: center; padding: 8px 0; font-size: 13px; color: #999;">加载中...</div>
            <div v-else-if="!dialogTableHasNext && dialogPrizeData.length > 0" style="text-align: center; padding: 8px 0; font-size: 13px; color: #999;">没有更多数据了</div>
          </template>
        </el-table>
      </div>
    </div>
    <template #footer>
      <div class="flex_center" style="gap: 20px;">
        <v-btn variant="elevated" @click="handleCloseBindPrize">
          <template v-slot:default>
            <div class="flex_bt">
              <v-icon style="margin-right: 4px;" color="#000">mdi-close</v-icon>
              <span>关闭</span>
            </div>
          </template>
        </v-btn>
        <v-btn variant="elevated" color="primary" @click="handleSelectPrize" :disabled="selectedPrizeRows.length === 0">
          <template v-slot:default>
            <div class="flex_bt">
              <v-icon style="margin-right: 4px;" color="#fff">mdi-check</v-icon>
              <span style="color: #fff;">选择商品</span>
            </div>
          </template>
        </v-btn>
      </div>
    </template>
  </el-dialog>
</template>

<style lang="scss" scoped>
.page-container {
  padding: 16px 24px;
}

.top-bar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding-bottom: 16px;
}

.page-title {
  font-size: 20px;
  font-weight: 600;
  color: #272539;
  margin: 0;
}

.form-wrapper {
  padding: 24px;
  border-radius: 4px;
}

.add-form {
  max-width: 600px;
}

.form-value {
  font-size: 14px;
  color: #606266;
}

.dialog-content {
  display: flex;
  gap: 16px;
  margin-top: 16px;
  min-height: 400px;
}

.dialog-tree-panel {
  width: 280px;
  min-width: 280px;
  height: 460px;
  overflow-y: auto;
  border: 1px solid #e8e8e9;
  border-radius: 8px;
  padding: 12px;
  box-sizing: border-box;
}

.dialog-table-panel {
  flex: 1;
  height: 460px;
  overflow-y: auto;
  border: 1px solid #e8e8e9;
  border-radius: 8px;
  padding: 12px;
  box-sizing: border-box;
}

.custom-tree-node {
  display: flex;
  align-items: center;
  font-size: 14px;
}

.image-placeholder-sm {
  width: 50px;
  height: 50px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: #f5f5f5;
  color: #999;
  font-size: 11px;
}
</style>
